const pad = (n: number): string => String(n).padStart(2, "0");

/** Formats a local Date as YYYY-MM-DD (no timezone shift). */
export const formatLocalIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Parses YYYY-MM-DD into a local Date (no timezone shift). Returns null when invalid. */
export const parseLocalIsoDate = (value?: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.getFullYear() === Number(y) && date.getMonth() === Number(m) - 1 && date.getDate() === Number(d)
    ? date
    : null;
};

/** Largest supported "± days" offset; keeps the resolved dates inside the valid Date range. */
export const MAX_PLUS_MINUS_DAYS = 365;

/** Clamps a custom day offset to an integer within 0..MAX_PLUS_MINUS_DAYS. Returns null for non-numeric input. */
export const clampPlusMinusDays = (days: number): number | null =>
  Number.isFinite(days) ? Math.min(Math.max(Math.trunc(days), 0), MAX_PLUS_MINUS_DAYS) : null;

/**
 * Resolves "today ± n days" into a YYYY-MM-DD range using local-date arithmetic.
 * Days are clamped to 0..MAX_PLUS_MINUS_DAYS. Returns null when the days or the resulting dates are invalid.
 */
export const resolvePlusMinusDays = (today: Date, days: number): { startDate: string; endDate: string } | null => {
  const clamped = clampPlusMinusDays(days);
  if (clamped === null) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - clamped);
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + clamped);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { startDate: formatLocalIsoDate(start), endDate: formatLocalIsoDate(end) };
};

/** Formats a resolved range as `D MMM YYYY → D MMM YYYY`. Returns "" when either bound is missing/invalid. */
export const formatRangeHint = (startDate?: string, endDate?: string, locale = "en"): string => {
  const start = parseLocalIsoDate(startDate);
  const end = parseLocalIsoDate(endDate);
  if (!start || !end) return "";
  const month = (date: Date) => new Intl.DateTimeFormat(locale, { month: "short" }).format(date);
  const format = (date: Date) => `${date.getDate()} ${month(date)} ${date.getFullYear()}`;
  return `${format(start)} → ${format(end)}`;
};
